import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "../../components/ui/button";

/**
 * One full-width, full-bleed product showcase section.
 *
 * Layout: 2 columns on lg+, stacked on smaller screens. Direction alternates
 * via the `reverse` prop so consecutive products flip the visual/text sides.
 *
 * Visual side renders the product's `visual` component (a mock dashboard).
 * Text side carries eyebrow / title / tagline / description / capabilities
 * and primary CTA.
 *
 * All accent-colored elements derive from the product's accent palette so
 * each product feels uniquely branded without forking the layout.
 */
export default function ProductShowcase({ product, index, total, reverse }) {
  const { accent } = product;
  const Visual = product.visual;
  const indexLabel = String(index + 1).padStart(2, "0");
  const totalLabel = String(total).padStart(2, "0");

  return (
    <section
      id={product.id}
      className="relative scroll-mt-20 overflow-hidden bg-[#080d16] px-4 py-14 sm:px-6 sm:py-16 lg:scroll-mt-24 lg:py-24 xl:py-28"
    >
      <div
        className="pointer-events-none absolute -z-0 h-[640px] w-[640px] rounded-full blur-[140px]"
        style={{
          backgroundColor: accent.glow,
          top: "-200px",
          left: reverse ? "auto" : "-180px",
          right: reverse ? "-180px" : "auto",
          opacity: 0.55,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -z-0 h-[440px] w-[440px] rounded-full blur-[120px]"
        style={{
          backgroundColor: accent.glow,
          bottom: "-160px",
          left: reverse ? "-120px" : "auto",
          right: reverse ? "auto" : "-120px",
          opacity: 0.35,
        }}
        aria-hidden
      />

      <div
        className="pointer-events-none absolute inset-0 -z-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)",
          backgroundSize: "32px 32px",
          maskImage:
            "radial-gradient(ellipse 70% 80% at 50% 50%, #000 20%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 80% at 50% 50%, #000 20%, transparent 80%)",
        }}
        aria-hidden
      />

      <div className="relative mx-auto grid max-w-6xl items-center gap-10 sm:gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className={reverse ? "lg:order-2" : "lg:order-1"}
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span
              className="text-[11px] font-bold uppercase tracking-[0.22em] tabular-nums"
              style={{ color: accent.light }}
            >
              {indexLabel}
              <span className="mx-1.5 text-white/30">/</span>
              <span className="text-white/55">{totalLabel}</span>
            </span>
            <span
              className="hidden h-px max-w-[80px] flex-1 bg-gradient-to-r from-white/15 to-transparent sm:block"
              aria-hidden
            />
            <span
              className="rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] sm:text-[10.5px]"
              style={{
                color: accent.light,
                backgroundColor: accent.bg,
                borderColor: accent.border,
              }}
            >
              {product.category}
            </span>
          </div>

          <div className="mt-6 sm:mt-7">
            {product.logoAsName ? (
              <>
                <h2 className="sr-only">{product.name}</h2>
                {/* PNG ships with an opaque black plate; screen-blend removes it on dark UI */}
                <img
                  src={product.logo}
                  alt={product.name}
                  width="280"
                  height="56"
                  className="block h-10 w-auto max-w-[220px] object-contain object-left mix-blend-screen sm:h-12 sm:max-w-[260px] lg:h-14 lg:max-w-[300px]"
                  loading="eager"
                  decoding="async"
                />
              </>
            ) : (
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Logos are tightly cropped to their visible mark; we apply a
                   gentle saturation bump and an accent-tinted halo so they
                   lift off the near-black showcase bg. */}
                <img
                  src={product.logo}
                  alt=""
                  aria-hidden
                  width="64"
                  height="64"
                  className="h-12 w-12 shrink-0 object-contain sm:h-14 sm:w-14 lg:h-16 lg:w-16"
                  style={{
                    filter: `saturate(1.1) drop-shadow(0 12px 22px rgba(0,0,0,0.5)) drop-shadow(0 0 26px ${accent.glow})`,
                  }}
                  loading="eager"
                  decoding="async"
                />
                <h2 className="text-[clamp(1.875rem,4.4vw,3rem)] font-extrabold leading-[1.04] tracking-tight text-white">
                  {product.name}
                </h2>
              </div>
            )}
            <p
              className="mt-2.5 text-[0.98rem] font-semibold leading-snug sm:mt-3 sm:text-[1.08rem]"
              style={{ color: accent.light }}
            >
              {product.tagline}
            </p>
          </div>

          <p className="mt-4 max-w-xl text-[0.94rem] leading-[1.7] text-white/[0.88] sm:mt-5 sm:text-[0.98rem] sm:leading-[1.75]">
            {product.description}
          </p>

          <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3 sm:mt-7 sm:grid-cols-2 sm:gap-y-2.5">
            {product.capabilities.map((cap, i) => (
              <motion.li
                key={cap}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.4, delay: 0.15 + i * 0.05 }}
                className="flex items-start gap-2.5 text-[0.875rem] leading-snug text-white/[0.92]"
              >
                <span
                  className="mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center rounded-md ring-1"
                  style={{
                    backgroundColor: accent.bg,
                    color: accent.primary,
                    borderColor: accent.border,
                  }}
                >
                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                </span>
                <span>{cap}</span>
              </motion.li>
            ))}
          </ul>

          <div className="mt-7 sm:mt-9">
            <Button
              asChild
              size="lg"
              className="group h-11 border-0 px-6 text-[13px] font-bold text-[#0a0f18] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 sm:h-12 sm:px-7 sm:text-[13.5px]"
              style={{
                background: `linear-gradient(135deg, ${accent.light}, ${accent.primary})`,
                boxShadow: `0 10px 36px -10px ${accent.glow}`,
              }}
            >
              <Link to="/contact" state={{ product: product.name }}>
                Request a Demo
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>
        </motion.div>

        <div className={reverse ? "lg:order-1" : "lg:order-2"}>
          <Visual accent={accent} />
        </div>
      </div>
    </section>
  );
}
